import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, X } from 'lucide-react';
import { 
  useCreateLegalDocument, 
  useUpdateLegalDocument, 
  useUploadLegalDocumentPDF,
  type LegalDocument 
} from '@/hooks/useLegalDocuments';
import { toast } from 'sonner';

const schema = z.object({
  title: z.string().min(1, 'Title is required'),
  document_type: z.string().min(1, 'Document type is required'),
  version: z.string().min(1, 'Version is required'),
  status: z.enum(['draft', 'review', 'published', 'archived']),
  content: z.string().optional(),
  is_active: z.boolean(),
  effective_date: z.string().optional(),
  expiry_date: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface LegalDocumentFormProps {
  document?: LegalDocument | null;
  onClose: () => void;
}

const LegalDocumentForm = ({ document, onClose }: LegalDocumentFormProps) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const createMutation = useCreateLegalDocument();
  const updateMutation = useUpdateLegalDocument();
  const uploadMutation = useUploadLegalDocumentPDF();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: document?.title || '',
      document_type: document?.document_type || '',
      version: document?.version || '1.0',
      status: document?.status || 'draft',
      content: document?.content || '',
      is_active: document?.is_active || false,
      effective_date: document?.effective_date ? 
        new Date(document.effective_date).toISOString().split('T')[0] : '',
      expiry_date: document?.expiry_date ? 
        new Date(document.expiry_date).toISOString().split('T')[0] : '',
    },
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && file.type === 'application/pdf') {
      setUploadedFile(file);
    } else {
      toast.error('Please upload a PDF file');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
  });

  const removeFile = () => {
    setUploadedFile(null);
  };

  const onSubmit = async (data: FormData) => {
    try {
      let pdfUrl = document?.pdf_url;
      let filePath = document?.file_path;
      let fileSize = document?.file_size;

      // Upload PDF if a new file was selected
      if (uploadedFile) {
        setIsUploading(true);
        const fileName = `${data.document_type}_${Date.now()}.pdf`;
        const uploadResult = await uploadMutation.mutateAsync({
          file: uploadedFile,
          path: fileName,
        });
        pdfUrl = uploadResult.url;
        filePath = uploadResult.path;
        fileSize = uploadResult.size;
        setIsUploading(false);
      }

      const documentData = {
        ...data,
        pdf_url: pdfUrl,
        file_path: filePath,
        file_size: fileSize,
        effective_date: data.effective_date ? new Date(data.effective_date).toISOString() : null,
        expiry_date: data.expiry_date ? new Date(data.expiry_date).toISOString() : null,
      };

      if (document) {
        await updateMutation.mutateAsync({ id: document.id, ...documentData });
      } else {
        await createMutation.mutateAsync(documentData);
      }

      onClose();
    } catch (error) {
      // Error handled by notification system
    }
  };

  const documentTypes = [
    { value: 'privacy_policy', label: 'Privacy Policy' },
    { value: 'terms_of_service', label: 'Terms of Service' },
    { value: 'cookie_policy', label: 'Cookie Policy' },
    { value: 'data_protection', label: 'Data Protection' },
    { value: 'user_agreement', label: 'User Agreement' },
    { value: 'refund_policy', label: 'Refund Policy' },
    { value: 'accessibility_statement', label: 'Accessibility Statement' },
    { value: 'community_guidelines', label: 'Community Guidelines' },
    { value: 'custom', label: 'Custom' },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="title">Title</Label>
          <Input 
            id="title" 
            {...register('title')} 
            placeholder="e.g., Privacy Policy"
          />
          {errors.title && (
            <p className="text-sm text-destructive">{errors.title.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="document_type">Document Type</Label>
          <Select 
            value={watch('document_type')} 
            onValueChange={(value) => setValue('document_type', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {documentTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.document_type && (
            <p className="text-sm text-destructive">{errors.document_type.message}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="version">Version</Label>
          <Input 
            id="version" 
            {...register('version')} 
            placeholder="1.0"
          />
          {errors.version && (
            <p className="text-sm text-destructive">{errors.version.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select 
            value={watch('status')} 
            onValueChange={(value: any) => setValue('status', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="review">Under Review</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="is_active">Active</Label>
          <div className="flex items-center space-x-2 pt-2">
            <Switch
              checked={watch('is_active')}
              onCheckedChange={(checked) => setValue('is_active', checked)}
            />
            <span className="text-sm text-muted-foreground">
              {watch('is_active') ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="effective_date">Effective Date</Label>
          <Input 
            id="effective_date" 
            type="date" 
            {...register('effective_date')} 
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="expiry_date">Expiry Date</Label>
          <Input 
            id="expiry_date" 
            type="date" 
            {...register('expiry_date')} 
          />
        </div>
      </div>

      <Tabs defaultValue="content" className="w-full">
        <TabsList>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="pdf">PDF Document</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="content">HTML Content</Label>
            <Textarea 
              id="content" 
              {...register('content')} 
              rows={12}
              placeholder="Enter the legal document content in HTML format..."
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              You can use HTML tags for formatting. This content will be displayed on the public legal pages.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="pdf" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>PDF Document</CardTitle>
            </CardHeader>
            <CardContent>
              {document?.pdf_url && !uploadedFile && (
                <div className="mb-4 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm">Current PDF attached</span>
                    </div>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => window.open(document.pdf_url, '_blank')}
                    >
                      View
                    </Button>
                  </div>
                </div>
              )}

              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive 
                    ? 'border-primary bg-primary/5' 
                    : 'border-muted-foreground/25 hover:border-primary/50'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                {isDragActive ? (
                  <p className="text-muted-foreground">Drop the PDF file here...</p>
                ) : (
                  <div>
                    <p className="text-muted-foreground mb-2">
                      Drag and drop a PDF file here, or click to select
                    </p>
                    <p className="text-xs text-muted-foreground">
                      PDF files only, max 10MB
                    </p>
                  </div>
                )}
              </div>

              {uploadedFile && (
                <div className="mt-4 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm">{uploadedFile.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={removeFile}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={isSubmitting || isUploading}
        >
          {isSubmitting || isUploading ? 'Saving...' : (document ? 'Update' : 'Create')}
        </Button>
      </div>
    </form>
  );
};

export default LegalDocumentForm;